import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Synaps } from '@synaps-io/verify-sdk'
import { KYCContainer, Section, SectionTitle } from './styles'
import { useDispatch, useSelector } from 'react-redux'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import { Box } from '../../../shared_components/UI/Box'
import { Icon } from '../../../shared_components/Page'
import { selectUser } from '../../../modules/user/selectors'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import { theseus, theseus2 } from '../../../api/theseus'
import { KYCStatus, setLithiumAccount } from '../../../modules/user/slice'
import { missionControlApiSlice } from '../../../modules/missionControl/api'
import { set } from 'ramda'
import styled from 'styled-components'

interface Props {
  hasPassedKYC: boolean
  setHasPassedKYC: Dispatch<SetStateAction<boolean>>
  accountPage?: boolean
}

const KYC: FC<Props> = ({
  hasPassedKYC,
  setHasPassedKYC,
  accountPage = false,
}) => {
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)
  const [failureReasons, setFailureReasons] = useState([])
  const [shouldCheckStatus, setShouldCheckStatus] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [sessionId, setSessionId] = useState('')

  const [hasError, setHasError] = useState(false)
  const [kycStatus, setKycStatus] = useState<string | null>(null)

  const user = useSelector(selectUser)
  const dispatch = useDispatch()

  const notifyApiOfKycComplete = useCallback(async () => {
    if (!user) return
    try {
      await theseus2.get('/user/kyc/check-session')
    } catch (error) {
      console.error(error)
    }
  }, [user])

  const initSynaps = useCallback(
    (session_id: string) => {
      Synaps.init({
        sessionId: session_id,
        walletAddress: user?.walletAddress,
        onFinish: () => {
          notifyApiOfKycComplete()
          setShouldCheckStatus(true)
        },
        mode: 'modal',
      })
    },
    [notifyApiOfKycComplete, user?.walletAddress],
  )

  const fetchSessionId = useCallback(async () => {
    setShouldCheckStatus(false)
    try {
      if (!user) return

      let session_id = user.kycSessionId
      if (!session_id) {
        const { data } = await theseus2.post('/user/kyc/create-session')
        const { session_id: idFromApi } = data
        session_id = idFromApi
      }
      setSessionId(session_id)
      initSynaps(session_id)
    } catch (error) {
      console.error(error)
    }
  }, [user, initSynaps])

  function startKyc() {
    Synaps.show()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const checkKycStatus = useCallback(async () => {
    if (!sessionId) {
      fetchSessionId()
      return
    }
    try {
      setIsLoading(true)
      if (!user || !sessionId) return
      const {
        data: { user: userFromFreshRequest },
      } = await theseus.get('/auth/b2c/verify')

      const { kycStatus } = userFromFreshRequest

      const hasChanged = !isDeepEqual(user, userFromFreshRequest)

      if (hasChanged) dispatch(setLithiumAccount(userFromFreshRequest))

      if (kycStatus === KYCStatus.APPROVED_IN_CONTRACT) {
        setHasCheckedStatus(true)
        setShouldCheckStatus(false)
        setHasPassedKYC(true)
        return
      }
      if (kycStatus === KYCStatus.FAILED) {
        setHasFailed(true)
        // TODO: Get failure reasons from synaps on theseus and set
        setFailureReasons(user?.kycFailureReasons ?? [])
        setHasCheckedStatus(true)
        setShouldCheckStatus(false)
        setHasPassedKYC(false)
        return
      }

      if (kycStatus === KYCStatus.RESUBMISSION_REQUIRED) {
        setHasFailed(true)
        setFailureReasons(user?.kycFailureReasons ?? [])
        setHasCheckedStatus(true)
        setShouldCheckStatus(false)
        setKycStatus(kycStatus)
        return
      }

      const isPending =
        kycStatus === KYCStatus.APPROVED_WITH_PARTNER ||
        kycStatus === KYCStatus.PENDING

      if (isPending) {
        setHasCheckedStatus(true)
        setShouldCheckStatus(false)
        setHasPassedKYC(false)
        setKycStatus(kycStatus)
        return
      }
      if (!kycStatus) {
        setHasCheckedStatus(true)
        setShouldCheckStatus(false)
        setHasPassedKYC(false)
      }
    } catch (error) {
      console.error(error)
      setHasCheckedStatus(true)
    } finally {
      setIsLoading(false)
    }
  }, [user, sessionId, fetchSessionId, setHasPassedKYC, dispatch])

  useEffect(() => {
    if (isLoading) return
    fetchSessionId()
  }, [sessionId, isLoading, fetchSessionId])

  useEffect(() => {
    if (!user) return
    checkKycStatus()
  }, [user, checkKycStatus, sessionId])

  const KYC_POLL_INTERVAL = 2500

  useEffect(() => {
    const pollKyc = setInterval(checkKycStatus, KYC_POLL_INTERVAL)
    if (!shouldCheckStatus) clearInterval(pollKyc)
    return () => {
      clearInterval(pollKyc)
    }
  }, [shouldCheckStatus, checkKycStatus])

  const Passed = () => (
    <Box g={2} ac>
      <Icon
        smallMargin
        src={`/img/icons/${hasPassedKYC ? 'approved' : 'chain-warning'}.svg`}
        alt="exclaimation mark icon"
      />
      <h6>KYC Completed</h6>
    </Box>
  )
  const Failed = ({ canResubmit }: { canResubmit?: boolean }) => (
    <Box dc g={24}>
      <Box g={2} ac>
        <Icon
          smallMargin
          src={`/img/icons/${hasPassedKYC ? 'approved' : 'chain-warning'}.svg`}
          alt="exclaimation mark icon"
        />
        <h5>KYC Failed</h5>
      </Box>
      {!!failureReasons?.length && (
        <Box dc g={8}>
          <span>KYC Failed for the following reasons:</span>
          <ul>
            {failureReasons.map((reason, index) => (
              <li key={index}>{reason}</li>
            ))}
          </ul>
        </Box>
      )}
      {canResubmit && (
        <Box>
          <ThemedButton onClick={startKyc} disabled={isLoading || !sessionId}>
            Try again
          </ThemedButton>
        </Box>
      )}
    </Box>
  )
  const Pending = () => (
    <Box dc g={16}>
      <h5>KYC Pending</h5>
      <span className="highlight">
        Please allow up to 24 hours for this to complete
      </span>
    </Box>
  )

  const ShouldComplete = () => (
    <>
      <Box g={2} ac>
        <Icon
          smallMargin
          src={`/img/icons/${hasPassedKYC ? 'approved' : 'chain-warning'}.svg`}
          alt="exclaimation mark icon"
        />
        <h6>Verify Identity</h6>
      </Box>
      <span>
        In order to participate in this raise you’ll need to verify your
        identity, through our third party provider, Synaps.
      </span>

      <Box marginTop={16}>
        <ThemedButton onClick={startKyc} disabled={isLoading || !sessionId}>
          Verify Identity{' '}
        </ThemedButton>
      </Box>
    </>
  )

  const NoWallet = () => (
    <Box marginTop={2}>
      <h6>Connect your wallet to start KYC!</h6>
    </Box>
  )

  return (
    <Styles>
      <Section noBorder>
        {!accountPage && <SectionTitle>INVESTOR INFORMATION</SectionTitle>}
        {!user ? (
          <KYCContainer isApproved={hasPassedKYC}>
            <Icon
              smallMargin
              src={`/img/icons/${
                hasPassedKYC ? 'approved' : 'chain-warning'
              }.svg`}
              alt="exclaimation mark icon"
            />
            <NoWallet />
          </KYCContainer>
        ) : !hasCheckedStatus ? (
          <Box marginTop={32} justify="center" align="center">
            <AnimatedLoader size={48} onlyWhite />
          </Box>
        ) : kycStatus === KYCStatus.PENDING ||
          kycStatus === KYCStatus.APPROVED_WITH_PARTNER ||
          kycStatus === KYCStatus.APPROVED_WITH_PARTNER_PENDING_CONTRACT ? (
          <Pending />
        ) : kycStatus === KYCStatus.RESUBMISSION_REQUIRED ? (
          <Failed canResubmit />
        ) : accountPage && !hasPassedKYC ? (
          <Box marginTop={-32}>
            <ThemedButton onClick={startKyc} disabled={isLoading || !sessionId}>
              Verify Identity{' '}
            </ThemedButton>
          </Box>
        ) : (
          <KYCContainer isApproved={hasPassedKYC}>
            <Box direction="column">
              {hasPassedKYC ? (
                <Passed />
              ) : hasFailed ? (
                <Failed />
              ) : (
                <ShouldComplete />
              )}
            </Box>
          </KYCContainer>
        )}
      </Section>
    </Styles>
  )
}

const Styles = styled.div`
  li {
    list-style-type: disc;
    margin-left: 16px;
  }
`

export default KYC

export function useKycStatus() {
  const user = useSelector(selectUser)
  const kycStatus = user?.kycStatus ?? 'REQUIRED'
  const kycComplete = kycStatus === KYCStatus.APPROVED_IN_CONTRACT
  const label =
    kycStatus === KYCStatus.APPROVED_IN_CONTRACT
      ? 'APPROVED'
      : kycStatus === KYCStatus.FAILED ||
        kycStatus === KYCStatus.RESUBMISSION_REQUIRED
      ? 'FAILED'
      : kycStatus === KYCStatus.PENDING ||
        kycStatus === KYCStatus.APPROVED_WITH_PARTNER
      ? 'PENDING'
      : 'REQUIRED'
  return {
    kycStatus,
    kycComplete,
    label,
    kycNotStarted: !kycStatus || kycStatus === 'REQUIRED',
  }
}

export function isDeepEqual(a: any, b: any): boolean {
  if (a === b) {
    return true
  }

  const isJSON = (str: string): boolean => {
    try {
      JSON.parse(str)
    } catch (e) {
      return false
    }
    return true
  }

  if (isJSON(JSON.stringify(a)) && isJSON(JSON.stringify(b))) {
    return JSON.stringify(a) === JSON.stringify(b)
  }

  return false
}
